import cn from 'classnames'
import Color from 'color'
import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { createUseStyles } from '../../helpers/createStyles'
import { theme } from '../../styles/theme'
import Link from '../Link'
import { resolveInternalLinkUrl } from '../../lib/resolveLink'
import Section from '../Section'
import RichContent from '../RichContent'
import get from 'lodash/get'
import useScrollTrigger from '../../hooks/useScrollTrigger'

const richContentOptions = { showExternalIcon: false }

const CareerListing = ({
  data: {
    manualRoles = [],
    automaticRoles = [],
    manualMode,
    enquiryRolesAvailable,
    enquiryNoRolesAvailable
  },
  page
}) => {
  const styles = useStyles()
  const roles = useMemo(
    () => (manualMode ? manualRoles : automaticRoles) || [],
    [manualRoles, automaticRoles, manualMode]
  )

  const enquiryContent = useMemo(
    () =>
      roles.length === 0 && enquiryNoRolesAvailable
        ? enquiryNoRolesAvailable
        : enquiryRolesAvailable,
    [roles, enquiryRolesAvailable, enquiryNoRolesAvailable]
  )

  const lightBorder = useMemo(
    () =>
      Color(theme.colors.pageTheme[get(page, 'color.title', 'default')].border)
        .alpha(0.5)
        .string(),
    [page]
  )

  const sectionRef = useScrollTrigger(
    () => ({
      trigger: sectionRef.current,
      start: () => `${window.innerHeight / 4}px bottom`,
      scrub: false
    }),
    (tl, ref) => {
      const defaults = {
        ease: 'power2.inOut',
        duration: 0.8
      }
      tl.from(ref.current.querySelectorAll(`.${styles.header}, .${styles.role}, .${styles.enquiry} > *`), { y: 40, opacity: 0, stagger: 0.05, ...defaults })
    }
  )

  return (
    <div
      className={cn(styles.root, {
        [styles.empty]: roles.length === 0
      })}
      ref={sectionRef}
    >
      {roles.length > 0 && (
        <Section grid className={styles.header} noBottomMargin>
          <span>Role</span>
          <span>Location</span>
          <span>Role type</span>
        </Section>
      )}
      {roles.map((role, index) => (
        <Section
          key={index}
          tag='article'
          grid
          className={styles.role}
          noBottomMargin={index < roles.length - 1}
          style={{ '--border': lightBorder }}
        >
          <h6 className={styles.title}>{role.title}</h6>
          <address className={styles.location}>{role.location}</address>
          <span className={styles.roleType}>{role.roleType}</span>
          <Link
            to={resolveInternalLinkUrl({
              parent: page,
              slug: role.slug,
              locale: page.locale
            })}
            className={styles.link}
          >
            Read more
          </Link>
        </Section>
      ))}
      <Section grid className={styles.enquiry}>
        <h3 className={styles.enquiryTitle}>Join us</h3>
        <RichContent options={richContentOptions} content={enquiryContent} />
      </Section>
    </div>
  )
}

const useStyles = createUseStyles({
  root: {
    marginBottom: theme.spacingPx(6),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacingPx(14)
    }
  },
  empty: {
    [theme.breakpoints.up('md')]: {
      '.intro-hero + &': {
        // Cancel-out Hero margin-bottom when empty on large devices
        marginTop: -(theme.section.marginBottom.md - theme.spacing(3))
      }
    },
    [theme.breakpoints.up('xl')]: {
      '.intro-hero + &': {
        // Cancel-out Hero margin-bottom when empty on large devices
        marginTop: -(theme.section.marginBottom.xl - theme.spacing(3))
      }
    }
  },
  header: {
    borderBottom: '1px solid var(--border)',
    marginBottom: theme.spacingPx(4),
    '& > span': {
      gridColumn: 'auto / span 3',
      fontWeight: 'bold',
      display: 'block',
      paddingBottom: theme.spacingPx(1)
    },
    [theme.breakpoints.down('md')]: {
      display: 'none!important'
    }
  },
  role: {
    borderTop: '1px solid var(--border)',
    padding: `${theme.spacingPx(2)} 0`,
    rowGap: '0!important',
    '& > *': {
      gridColumn: '1 / -1'
    },
    [theme.breakpoints.up('md')]: {
      padding: 0,
      height: 50,
      borderTop: 0,
      borderBottom: '1px solid var(--border)',
      display: 'grid',
      alignItems: 'center',
      '& > *': {
        gridColumn: 'auto / span 3'
      }
    }
  },
  title: {
    marginBottom: theme.spacingPx(2),
    font: 'inherit',
    [theme.breakpoints.up('md')]: {
      marginBottom: 0
    }
  },
  location: {
    font: 'inherit'
  },
  roleType: {
    display: 'block'
  },
  link: {
    [theme.breakpoints.up('md')]: {
      '.dialog-grid &': {
        gridColumnEnd: 'span 2'
      }
    }
  },
  enquiry: {
    rowGap: '0!important',
    '& > *': {
      gridColumn: '1 / -1',
      [theme.breakpoints.up('md')]: {
        gridColumnStart: '7'
      }
    }
  },
  enquiryTitle: {},
  enquiryContent: {}
})

CareerListing.propTypes = {
  data: PropTypes.shape({
    manualRoles: PropTypes.array,
    automaticRoles: PropTypes.array,
    manualMode: PropTypes.bool.isRequired,
    enquiryTitle: PropTypes.string.isRequired,
    enquiryRolesAvailable: PropTypes.array.isRequired,
    enquiryNoRolesAvailable: PropTypes.array.isRequired
  }).isRequired,
  page: PropTypes.object.isRequired
}

export default CareerListing
